import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, FilterTitle, IconWrapper } from '../Filters.style';
import { FilterHeader, VehicleTypeContentWrapper } from './VehicleTypeSelector.style';
import { FilterOption, FilterResultCounts, RangeResultsCounts } from '../../../types';
import { FilterTypes } from '../../../constants/initialFilters';
import Tabs from './Tabs';
import RadioButtonSelector from '../../RadioButtonSelector';
import InfoPopover from '../../InfoPopover';

interface VehicleTypeSelectorTypes {
  filterType: string;
  vehicleTypeOptions: {
    [key: string]: FilterOption;
  };
  usedInventoryTypeOptions: {
    [key: string]: FilterOption;
  };
  newInventoryTypeOptions: {
    [key: string]: FilterOption;
  };
  confirmedFilters: FilterTypes;
  handleAddFilter: (filterType: string, value: string) => void;
  availableFilters: {
    filterResults: FilterResultCounts;
    rangeResults: RangeResultsCounts;
  };
  isTier3page: boolean;
  nonAudiOption?: any;
  isVehiclesLoading: boolean;
}

const VehicleTypeSelector: React.FC<VehicleTypeSelectorTypes> = ({
  filterType,
  vehicleTypeOptions,
  usedInventoryTypeOptions,
  newInventoryTypeOptions,
  confirmedFilters,
  isTier3page,
  nonAudiOption,
  isVehiclesLoading,
  handleAddFilter,
}) => {
  const { t } = useTranslation();

  const selectedOption = useMemo(() => {
    const selectedVehicleType = confirmedFilters.vehicleType;
    return selectedVehicleType;
  }, [confirmedFilters]);

  const selectedUsedInventoryType = useMemo(() => {
    if (isTier3page && confirmedFilters.nonAudi === 'foreign-brand.yes') {
      return 'foreign-brand.yes';
    }
    const selectedVehicleType = confirmedFilters.usedInventoryType;
    return selectedVehicleType;
  }, [isTier3page, confirmedFilters.nonAudi, confirmedFilters.usedInventoryType]);

  const selectedNewInventoryType = useMemo(() => {
    const selectedVehicleType = confirmedFilters.availableInMonths;
    return selectedVehicleType;
  }, [confirmedFilters]);

  const handleChangeVehicleType = (newValue: string) => {
    handleAddFilter(filterType, newValue);
  };

  const handleChangeUsedFilterValue = (isNonAudiFilter: boolean | undefined, newValue: string) => {
    if (isNonAudiFilter) {
      handleAddFilter('nonAudi', newValue);
      return;
    }
    handleAddFilter('usedInventoryType', newValue);
  };

  // Track the previous confirmed vehicleType using useRef (persists across renders)
  const prevVehicleTypeRef = useRef(confirmedFilters.vehicleType);
  // Determine if vehicle type has changed since last render
  const didVehicleTypeChange = prevVehicleTypeRef.current !== confirmedFilters.vehicleType;
  // Update ref after comparison is made — ensures we only detect the change once
  useEffect(() => {
    if (didVehicleTypeChange) {
      prevVehicleTypeRef.current = confirmedFilters.vehicleType;
    }
  }, [confirmedFilters.vehicleType, didVehicleTypeChange]);
  // Final computed loading flag — only true if loading AND vehicle type has changed
  const showLoadingForUsed = isVehiclesLoading && didVehicleTypeChange;

  return (
    <Filter>
      <FilterHeader>
        <FilterTitle>
          {t(`vlp.filters.filterOptions.${filterType}.title`)}
          <IconWrapper>
            <InfoPopover
              description={t(`vlp.filters.filterOptions.vehicleType.disclaimer`)}
              iconSize={24}
              triggerTestId="vehicle-status-popover-trigger"
              iconType="info"
              IconWrapperSize={24}
            />
          </IconWrapper>
        </FilterTitle>
      </FilterHeader>
      <Tabs
        options={vehicleTypeOptions}
        selectedOption={selectedOption as 'new' | 'used'}
        onChange={handleChangeVehicleType}
      />
      <VehicleTypeContentWrapper>
        {selectedOption === 'new' && (
          <RadioButtonSelector
            options={newInventoryTypeOptions}
            value={selectedNewInventoryType.toString()}
            onChange={(_isNonAudiFilter, newValue) => {
              handleAddFilter('availableInMonths', newValue);
            }}
            title={t(`vlp.filters.filterOptions.newInventoryType.title`)}
            name="new-inventory-type-options"
          />
        )}
        {selectedOption === 'used' && (
          <RadioButtonSelector
            options={usedInventoryTypeOptions}
            value={selectedUsedInventoryType}
            isVehiclesLoading={showLoadingForUsed}
            onChange={handleChangeUsedFilterValue}
            title={t(`vlp.filters.filterOptions.vehicleType.preOwnedTitle`)}
            name="used-inventory-type-options"
          />
        )}
      </VehicleTypeContentWrapper>
    </Filter>
  );
};

export default VehicleTypeSelector;
